import * as React from "react";
import { cn } from "@/lib/utils";
import { WorkingLabel } from "@/components/counter/WorkingLabel";

/**
 * The counter's one button (DESIGN.md §6, §7).
 *
 * Every screen had its own <button className="..."> before this: 217 of them,
 * each choosing its own height, radius and fill. One component means one place
 * decides that a target is 48 px, a tender is 56, the primary action is
 * fill.brand and a destructive action is the only alert on the screen.
 *
 *   brand        the ONE primary action per screen: Add, Charge, Pay, Print
 *   secondary    everything beside it
 *   quiet        a text action inside a row: Remove, Edit
 *   destructive  void, cancel, remove a line — at most one per screen
 */
export type CounterButtonTone = "brand" | "secondary" | "quiet" | "destructive" | "on-rail";
export type CounterButtonSize = "dense" | "default" | "large" | "icon";

const TONE: Record<CounterButtonTone, string> = {
  brand: "bg-fill-brand text-text-on-fill hover:bg-fill-brand-hover active:bg-fill-brand-press",
  secondary: "bg-fill-neutral text-text-strong border border-stroke-strong hover:bg-fill-selected",
  quiet: "bg-transparent text-text-brand hover:bg-fill-neutral",
  destructive: "bg-fill-error text-text-on-fill hover:bg-fill-error/90",
  // The rail and the customer display are the one dark ground in the app, and a
  // workspace tone is unreadable on them. Without this the rail kept its own
  // hand-rolled buttons, which is precisely the hole the kit exists to close.
  "on-rail": "bg-transparent text-text-on-rail hover:bg-stroke-on-rail hover:text-text-on-fill",
};

const SIZE: Record<CounterButtonSize, string> = {
  // 44 only inside dense lists; 48 is the floor everywhere else; 56 for tenders,
  // keypad keys and Add.
  dense: "min-h-target-dense px-3 text-sm",
  default: "min-h-target px-4 text-base",
  large: "min-h-target-lg px-5 text-base",
  // A square chrome button that holds a glyph and no words: the rail's collapse
  // toggle, a drawer's close. It sits below the touch floor on purpose — it is
  // pointer chrome on a fixed frame, never a till action.
  icon: "h-8 w-8 p-0 text-base",
};

export interface CounterButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  tone?: CounterButtonTone;
  size?: CounterButtonSize;
  /** Stretches to the panel width: tender buttons, the sheet's confirm. */
  block?: boolean;
}

/** Documents the counter button contract and behavior. */
export function CounterButton({
  tone = "secondary",
  size = "default",
  block = false,
  className,
  type = "button",
  ...props
}: Readonly<CounterButtonProps>) {
  return (
    <button
      type={type}
      className={cn(
        "inline-flex items-center justify-center gap-2 rounded-control font-sans font-semibold",
        "transition-colors duration-150 ease-counter",
        "disabled:bg-fill-disabled disabled:text-text-disabled disabled:cursor-not-allowed disabled:border-transparent",
        TONE[tone],
        SIZE[size],
        block && "w-full",
        className,
      )}
      {...props}
    />
  );
}

/**
 * The one strong action a screen ends on — Charge, Settle, Confirm, Print.
 * This is what the oxblood StampButton becomes: a full-width 56 px fill.brand
 * block. `busy` says "Working…" because a till that looks idle gets tapped
 * twice, and a disabled action must LOOK unavailable rather than silently
 * swallow the tap.
 *
 * Anything else a caller passes reaches the button, exactly as it does on
 * {@link CounterButton}. It used to be dropped: a `data-testid` on the desk's
 * "Save customer" never reached the DOM, and neither would an `aria-label` or a
 * `form` attribute. A component that quietly discards what it is handed is worse
 * than one that refuses it.
 */
export function PrimaryAction({
  children,
  onClick,
  type = "button",
  disabled,
  busy,
  tone = "brand",
  className,
  ...rest
}: Readonly<
  Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "onClick" | "type"> & {
    children: React.ReactNode;
    onClick?: () => void;
    type?: "button" | "submit";
    disabled?: boolean;
    busy?: boolean;
    /** `destructive` for the void/cancel screens — at most one per screen. */
    tone?: "brand" | "destructive";
    className?: string;
  }
>) {
  const off = disabled || busy;
  return (
    <CounterButton
      {...rest}
      type={type}
      tone={tone}
      size="large"
      block
      onClick={off ? undefined : onClick}
      disabled={off}
      aria-busy={busy || undefined}
      className={className}
    >
      {busy ? <WorkingLabel /> : children}
    </CounterButton>
  );
}
